import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

export default function MilestoneQuickBillDialog({ open, onOpenChange, po, milestone, onCreated }) {
    const navigate = useNavigate();
    const [terms, setTerms] = useState("30 days");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef("30 days");

    useEffect(() => {
        if (open) {
            setTerms("30 days");
            setSubmitting(false);
            baselineRef.current = "30 days";
        }
    }, [open]);

    const isDirty = terms !== baselineRef.current;

    if (!po || !milestone) return null;

    const bs = po.billing_status || { po_total: 0, balance_to_invoice: 0, invoiced_amount: 0 };
    const amount = Number.isFinite(milestone.expected_amount) ? milestone.expected_amount : 0;
    const balanceAfter = Math.max(0, Math.round((bs.balance_to_invoice - amount) * 100) / 100);
    const exceeds = amount - bs.balance_to_invoice > 0.01;
    const invalidAmount = !(amount > 0);

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/billing/invoice/from-po/${po.id}`, {
                milestone_id: milestone.id,
                payment_terms: terms,
                invoice_label: milestone.label,
            });
            toast.success(`Invoice ${data.invoice_number} generated for ${formatINR(data.total_amount)}`);
            navigate(`/invoices/${data.id}`);
            baselineRef.current = terms;  // bypass discard prompt
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to generate invoice"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-lg"
            testId="milestone-quickbill-dialog"
            title={<span className="flex items-center gap-2"><Receipt size={18} className="text-slate-700" weight="fill" />Bill milestone now</span>}
            description={`Confirm before raising the invoice. This will be persisted immutably and linked to ${po.po_number}.`}
        >

                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 space-y-2.5" data-testid="milestone-quickbill-preview">
                    <Row label="Milestone" testid="qb-milestone-label">
                        {milestone.label} <span className="text-slate-500 font-normal">({milestone.percent}% of PO)</span>
                    </Row>
                    <Row label="Purchase order">
                        <span className="font-mono text-xs font-bold text-slate-900">{po.po_number}</span>
                    </Row>
                    <div className="border-t border-slate-200 my-2" />
                    <Row label="Invoice amount (incl. GST)" testid="qb-amount" valueClass="reedos-data text-base font-bold text-slate-900">
                        {formatINR(amount)}
                    </Row>
                    <SmallRow label="PO total" value={formatINR(bs.po_total)} />
                    <SmallRow label="Already invoiced" value={formatINR(bs.invoiced_amount)} />
                    <SmallRow label="Remaining balance (before)" value={formatINR(bs.balance_to_invoice)} testid="qb-balance-before" valueClass="reedos-data text-amber-700 font-semibold" />
                    <SmallRow label="Remaining balance (after)" value={formatINR(balanceAfter)} testid="qb-balance-after"
                        valueClass={classJoin("reedos-data font-semibold", exceeds ? "text-rose-700" : "text-emerald-700")} />
                </div>

                {exceeds && (
                    <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800 font-semibold" data-testid="qb-exceeds-warning">
                        Milestone amount exceeds PO's remaining unbilled balance — cannot bill.
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Payment terms</Label>
                        <select
                            value={terms}
                            onChange={(e) => setTerms(e.target.value)}
                            className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                            data-testid="qb-terms-select"
                        >
                            <option>Immediate</option>
                            <option>15 days</option>
                            <option>30 days</option>
                            <option>45 days</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Invoice label</Label>
                        <Input value={milestone.label} disabled className="mt-1.5 bg-slate-50 disabled:opacity-100" />
                    </div>
                </div>

                <DialogFooter>
                    <FormDialogCancel testId="qb-cancel-btn">Cancel</FormDialogCancel>
                    <Button
                        onClick={submit}
                        disabled={submitting || exceeds || invalidAmount}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="qb-confirm-btn"
                    >
                        {submitting ? "Generating…" : `Confirm & generate ${formatINR(amount)}`}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
}

function Row({ label, children, testid, valueClass }) {
    return (
        <div className="flex items-center justify-between">
            <span className="reedos-eyebrow text-[10px]">{label}</span>
            <span className={classJoin("text-sm font-semibold text-slate-900", valueClass)} data-testid={testid}>{children}</span>
        </div>
    );
}

function SmallRow({ label, value, testid, valueClass }) {
    return (
        <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">{label}</span>
            <span className={classJoin("reedos-data text-slate-700", valueClass)} data-testid={testid}>{value}</span>
        </div>
    );
}
